import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import LineItemsEditor, { emptyRow } from "../../../../stock/components/LineItemsEditor";
import { QUOTATION_VAT_PERCENT } from "../../quotationVat";
import { useFillMissingUnits } from "../../../../stock/components/lineUnits";
import { useGetQuotationPrototypeQuery, useUpdateQuotationPrototypeMutation } from "../../quotationsApi";
import { paths } from "../../../../../routes/paths";

const toLineRequest = (row) => ({
  lineType: row.lineType || "PRODUCT",
  productCode: row.lineType === "FREETEXT" ? null : row.productCode || null,
  description: row.productName || row.productGroupName || "", // a group line with no wording prints as the group name
  qty: Number(row.qty) || 0,
  unit: row.unit || null,
  rate: Number(row.rate) || 0,
  vatPercent: Number(row.vatPercent) || 0,
  // Batch185 - only a free-text line carries a group tag; a product has its own group.
  productGroupId: row.lineType === "FREETEXT" && row.productGroupId ? Number(row.productGroupId) : null,
});

// Edit only - Create moved to PrototypeCreateModal.jsx (per direct
// instruction: the Line Items table needs more horizontal room than the
// split-view detail pane gives it, better suited to a wide modal).
const PrototypeForm = () => {
  const navigate = useNavigate();
  const { recordGuid } = useParams();

  const { data: existing } = useGetQuotationPrototypeQuery(recordGuid);
  const [updatePrototype, { isLoading: isUpdating }] = useUpdateQuotationPrototypeMutation();
  const fillMissingUnits = useFillMissingUnits();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setDescription(existing.description || "");
    const savedRows = existing.lines.map((l) => ({
      ...emptyRow(),
      key: `line-${l.lineId}`,
      lineType: l.lineType,
      productCode: l.productCode || "",
      productName: l.description,
      unit: l.unit || "",
      qty: String(l.qty),
      rate: String(l.rate),
      vatPercent: String(l.vatPercent),

      productGroupId: l.lineType === "FREETEXT" && l.productGroupId != null ? String(l.productGroupId) : "",
    }));
    setRows(savedRows);
    // Lines saved before the product got its unitLabel come back blank.
    fillMissingUnits(savedRows, existing.branchId).then((filled) => {
      if (filled !== savedRows) setRows(filled);
    });
  }, [existing, fillMissingUnits]);

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (rows.length === 0) return toast.error("At least one line item is required");

    try {
      await updatePrototype({
        recordGuid,
        name: name.trim(),
        description: description.trim() || null,
        lines: rows.map(toLineRequest),
      }).unwrap();
      toast.success("Prototype updated");
      navigate(paths.quotationPrototypeDetail(recordGuid));
    } catch (e) {
      toast.error(e?.message || "Could not save the prototype");
    }
  };

  if (!existing) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;

  // Rendered above AND below the grid - a 20-line prototype pushes the bottom pair far off screen.
  const actionButtons = (
    <div className="d-flex gap-2">
      <button type="button" className="btn btn-added" onClick={handleSubmit} disabled={isUpdating}>
        {isUpdating ? "Saving..." : "Save Changes"}
      </button>
      <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.quotationPrototypeDetail(recordGuid))}>Cancel</button>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <Link to={paths.quotationPrototypeDetail(recordGuid)} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Prototype</Link>
            <h4>Edit Quotation Prototype</h4>
            <h6>{existing.branchCode}</h6>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Name</label>
                <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Guarding" />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Description (optional)</label>
                <input type="text" className="form-control" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h6 className="mb-0">Line Items</h6>
              {actionButtons}
            </div>
            <LineItemsEditor
              branchId={existing.branchId}
              rows={rows}
              onChange={setRows}
              allowFreeText
              rateField="salesRate"
              columns={{ showRate: true, showVat: true, amountIncludesVat: true }}
              fixedVatPercent={QUOTATION_VAT_PERCENT}
            />
          </div>
        </div>

        <div className="mb-4">{actionButtons}</div>
      </div>
    </div>
  );
};

export default PrototypeForm;
